import { useEffect, useState } from 'react'

import { Box, Container, Grid } from '@mui/material'
import { GAME_STEP } from 'constants/enums/GameStepEnum'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { SUBMITTED_IDEAS } from 'constants/routes'
import { alertTitleTextMap } from 'content/alertText'
import { brainstormText } from 'content/brainstormText'
import { hasUnsavedIdeation } from 'utils/ideationUtils'
import logger from 'utils/logger'
import { useUnsavedIdeationWarning } from 'utils/useUnsavedIdeationWarning'

import { ViewMasterModel } from 'models/ViewMasterModel'
import { Game } from 'models/gameModels'

import { useIdeaStreakContext } from 'contexts/IdeaStreakProvider'
import { useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import { postViewMasterImage } from 'services/completions.service'
import { postIdea } from 'services/ideas.service'

import CustomGameTitleBar from 'components/CustomGameTitleBar/CustomGameTitleBar'
import { IdeationRecommendationModal } from 'components/IdeationRecommendationModal/IdeationRecommendationModal'

import Hero from '../../components/Hero/Hero'
import { styles } from './ViewMaster.styles'
import Section1 from './components/Section1/Section1'
import Section2 from './components/Section2'
import Section3 from './components/Section3'

export const TEST_ID = 'view-master'

const ViewMaster = ({ game }: { game?: Game }) => {
	const { setIdeaStreakCount } = useIdeaStreakContext()
	const [refreshClicksSinceIdeation, setRefreshClicksSinceIdeation] =
		useState(0)

	const {
		companyName,
		companyProblem,
		setCompanyName,
		setCompanyProblem,
		setGameId,
		authorIdeation,
		generatePromptsDisabled,
		setAuthorIdeation,
		termsAgreed,
		setTermsAgreed,
		submitIdeasDisabled,
		reset,
		imageData,
		setImageData,
		imageToJSON,
		postObject,
	} = ViewMasterModel()

	useEffect(() => {
		if (game) {
			setGameId(game.gameId)
			setCompanyName(game.innovationCompany)
			setCompanyProblem(game.companyProblem)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [game])

	const { showModal } = useModalContext()
	const [isAiLoading, setIsAiLoading] = useState(false)
	const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false)
	const isGameLoading = isAiLoading || isSubmitButtonLoading
	const [step, setStep] = useState<GAME_STEP>(GAME_STEP.Step1)
	const isUnsaved = hasUnsavedIdeation(step, authorIdeation)
	const { showAlert } = useToastContext()

	useUnsavedIdeationWarning(isUnsaved, authorIdeation, showModal)

	const resetAllField = () => {
		setCompanyProblem('')
		setCompanyName('')
	}

	const handlePrompts = async () => {
		setIsAiLoading(true)
		setStep(GAME_STEP.Step2)
		localStorage.setItem('innovationCompany', companyName)
		localStorage.setItem('companyIdea', companyProblem)

		try {
			const response = await postViewMasterImage(imageToJSON())
			if (
				response.status !== 200 ||
				typeof response.data !== 'string' ||
				!response.data
			) {
				throw new Error(
					`Failed to generate image. Status: ${response.status}`
				)
			}

			setImageData(response.data)
		} catch (error) {
			logger.error(error)
			showAlert?.({
				title: alertTitleTextMap.imageGenerationError.title,
				bodyText: alertTitleTextMap.imageGenerationError.text,
				severity: ToastSeverity.ERROR,
			})
		} finally {
			setIsAiLoading(false)
		}
	}

	const refreshImage = async () => {
		if (generatePromptsDisabled()) return

		if (refreshClicksSinceIdeation < 2) {
			await handlePrompts()
		}

		setRefreshClicksSinceIdeation((prev) => prev + 1)
	}

	const onIdeationRecommendationBackClicked = () => {
		setStep(GAME_STEP.Step1)
		onIdeationRecommendationModalClosed()
	}

	const onIdeationRecommendationModalClosed = () => {
		setRefreshClicksSinceIdeation(0)
	}

	const viewSubmittedIdeas = () => {
		window.open(`${window.location.origin}${SUBMITTED_IDEAS}`, '_blank')
	}

	const handleSubmitIdeas = async () => {
		setIsSubmitButtonLoading(true)

		try {
			const response = await postIdea(postObject())
			if (response.status !== 200) {
				throw new Error(
					`Failed to submit idea. Status: ${response.status}`
				)
			}

			showAlert?.({
				severity: ToastSeverity.SUCCESS,
			})
			setIdeaStreakCount((prevState) => prevState + 1)
			setRefreshClicksSinceIdeation(0)
			reset()

			window.scrollTo({
				top: 0,
			})
			setStep(GAME_STEP.Step1)
		} catch (error) {
			logger.error(error)
			if (showAlert) {
				showAlert({
					title: alertTitleTextMap.submitIdeaError.title,
					bodyText: alertTitleTextMap.submitIdeaError.text,
					severity: ToastSeverity.ERROR,
				})
			}
		} finally {
			setIsSubmitButtonLoading(false)
		}
	}

	useEffect(() => {
		setRefreshClicksSinceIdeation(0)
	}, [authorIdeation])

	return (
		<Box>
			<IdeationRecommendationModal
				refreshClicksSinceIdeation={refreshClicksSinceIdeation}
				onBack={onIdeationRecommendationBackClicked}
				onClose={onIdeationRecommendationModalClosed}
			/>
			<Hero
				data-testid="innovation-modal"
				step={step}
				gameTypeEnum={GAME_TYPE.VIEW_MASTER}
				innovationCompany={companyName}
				companyProblem={companyProblem}
				setInnovationCompany={setCompanyName}
				setCompanyProblem={setCompanyProblem}
				isLoading={isGameLoading}
				createPrompt={handlePrompts}
				hideEdit={Boolean(game && (game.gameTitle || game.endTime))}
			/>
			<Container
				data-testid={`${TEST_ID}-container`}
				maxWidth={step === GAME_STEP.Step1 ? 'md' : 'lg'}
				sx={styles.container}
			>
				{game && (game.gameTitle || game.endTime) && (
					<CustomGameTitleBar game={game} />
				)}
				{step === GAME_STEP.Step1 && (
					<Section1
						isLoading={isGameLoading}
						companyName={companyName}
						setCompanyName={setCompanyName}
						companyIdea={companyProblem}
						setCompanyIdea={setCompanyProblem}
						generatePromptsDisabled={generatePromptsDisabled}
						resetAllField={resetAllField}
						createPrompt={handlePrompts}
						editable={
							game
								? game.gameTitle === '' && game.endTime === ''
								: true
						}
						navigateTo={(path: string) =>
							window.open(
								`${window.location.origin}${path}`,
								'_blank'
							)
						}
					/>
				)}
				{step === GAME_STEP.Step2 && (
					<>
						<Grid container>
							<Grid item xs={12} md={6}>
								<Section2
									loading={isGameLoading}
									imageData={imageData}
									generatePromptsDisabled={
										generatePromptsDisabled
									}
									refreshImage={refreshImage}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Section3
									submitIdeasDisabled={submitIdeasDisabled}
									loading={isGameLoading}
									submitIdeas={handleSubmitIdeas}
									authorIdeation={authorIdeation}
									setAuthorIdeation={setAuthorIdeation}
									viewSubmittedIdeas={viewSubmittedIdeas}
									cautionText={
										game?.gameTitle || game?.endTime
											? brainstormText.global
													.alternateWarningText
											: brainstormText.global.warningText
									}
									termsAgreed={termsAgreed}
									setTermsAgreed={setTermsAgreed}
									gameId={game?.gameId}
								/>
							</Grid>
						</Grid>
					</>
				)}
			</Container>
		</Box>
	)
}

export default ViewMaster
